import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { nb } from '@/i18n/nb';
import type { CabinDocument } from '@/types/api';

export const documentKey = (slug: string) => ['document', slug] as const;

/*
 * The DB-backed documents editable from the admin Innhold list. Iter 4 ships om-hytta;
 * Iter 5 appends the three Hytteregler slugs (same table, same editor). previewVariant
 * 'display' gives Om hytta its Great Vibes script preview, matching its public view.
 */
export const EDITABLE_DOCUMENTS = [
  { slug: 'om-hytta', title: nb.modules.omHytta.title, previewVariant: 'display' as const },
  { slug: 'ordensregler', title: nb.documents.regler.subcards.ordensregler, previewVariant: 'default' as const },
  { slug: 'avreiserutiner', title: nb.documents.regler.subcards.avreiserutiner, previewVariant: 'default' as const },
  { slug: 'stengerutiner', title: nb.documents.regler.subcards.stengerutiner, previewVariant: 'default' as const },
];

// Public read — shared cache key, so an admin's save is reflected in the public overlay too.
export function useCabinDocument(slug: string) {
  return useQuery({
    queryKey: documentKey(slug),
    queryFn: async () => (await api.get<{ data: CabinDocument }>(`/documents/${slug}`)).data,
  });
}

// Admin save. A stale `version` returns 409 (ApiRequestError.status) — the editor page
// turns that into the conflict banner. On success the fresh document (new version) is
// pushed straight into the cache so editor + public view update without a refetch.
export function useUpdateCabinDocument(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { content: string; version: number }) =>
      api.patch<{ data: CabinDocument }>(`/documents/${slug}`, input),
    onSuccess: (response) => {
      queryClient.setQueryData(documentKey(slug), response.data);
    },
  });
}
